import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';

import { catalogApi } from 'api/catalog';
import { CustomerGuaranteeDaysByCompanySourceFields } from 'components/catalog/components/CustomerGuaranteeDaysByCompanySourceFields';
import {
    areAllCustomerGuaranteeDaysFilled,
    buildCustomerGuaranteeDaysByCompanySourcePayload,
    buildCustomerGuaranteeDaysChips,
    buildCustomerGuaranteeDaysPrefill,
} from 'components/catalog/utils/customerGuaranteeDaysByCompanySource';

function getAttachedCompanySources(row) {
    return (row.companySources ?? []).filter((source) => {
        const id = source.value ?? source.companySourceId ?? source.id;
        return id != null;
    });
}

function getCompanySourceIDs(companySources) {
    return companySources.map((source) => Number(source.value ?? source.companySourceId ?? source.id));
}

function getCompanySourceOptions(companySources) {
    return companySources.map((source) => {
        const id = Number(source.value ?? source.companySourceId ?? source.id);
        return {
            id,
            displayName: source.text || source.companySourceName || `Company source ${id}`,
        };
    });
}

function CustomerGuaranteeDaysCell({ row, onRefresh, setNotification }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [valuesByCompanySourceID, setValuesByCompanySourceID] = useState({});
    const [showError, setShowError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const companySources = getAttachedCompanySources(row);
    const companySourceIDs = getCompanySourceIDs(companySources);
    const companySourceOptions = getCompanySourceOptions(companySources);
    const chips = buildCustomerGuaranteeDaysChips({
        entries: row.customerGuaranteeDaysByCompanySource,
        companySources,
    });
    const canEdit = companySourceIDs.length > 0;

    const openEditor = () => {
        // Only prefill from attributes table rows — do not fall back to legacy item_information CGD.
        setValuesByCompanySourceID(
            buildCustomerGuaranteeDaysPrefill({
                companySourceIDs,
                customerGuaranteeDaysByCompanySource: row.customerGuaranteeDaysByCompanySource,
            }),
        );
        setShowError(false);
        setModalOpen(true);
    };

    const handleClose = () => {
        if (isSaving) {
            return;
        }
        setModalOpen(false);
        setShowError(false);
    };

    const handleSave = async () => {
        if (!areAllCustomerGuaranteeDaysFilled(valuesByCompanySourceID, companySourceIDs)) {
            setShowError(true);
            return;
        }

        setIsSaving(true);
        const payload = buildCustomerGuaranteeDaysByCompanySourcePayload(
            valuesByCompanySourceID,
            companySourceIDs,
        );
        const response = await catalogApi.upsertCustomerGuaranteeDaysByCompanySource(row.externalSku, payload);
        setIsSaving(false);

        if (!response.status) {
            setNotification({
                text: response.msg || 'Failed to update customer guarantee days',
                level: 'error',
            });
            return;
        }

        setModalOpen(false);
        setShowError(false);
        onRefresh();
        setNotification({
            text: 'Customer guarantee days updated',
            level: 'success',
        });
    };

    return (
        <>
            <Dialog open={modalOpen} fullWidth maxWidth="sm" onClose={handleClose}>
                <DialogTitle>Edit customer guarantee days</DialogTitle>
                <DialogContent>
                    <CustomerGuaranteeDaysByCompanySourceFields
                        companySourceIDs={companySourceIDs}
                        companySourceOptions={companySourceOptions}
                        valuesByCompanySourceID={valuesByCompanySourceID}
                        onChange={setValuesByCompanySourceID}
                        disabled={isSaving}
                        showError={showError}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button color="primary" variant="contained" onClick={handleSave} disabled={isSaving || !canEdit}>
                        {isSaving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Box textAlign="center">
                {chips.map((chip) => (
                    <Chip
                        key={chip.companySourceID}
                        size="small"
                        color="primary"
                        label={chip.label}
                        style={{ margin: '2px 1px' }}
                    />
                ))}
                {canEdit ? (
                    <Chip
                        size="small"
                        clickable
                        color="secondary"
                        style={{ margin: '2px' }}
                        label={chips.length === 0 ? 'Edit' : '✎'}
                        onClick={(event) => {
                            event.stopPropagation();
                            openEditor();
                        }}
                        aria-label="Edit customer guarantee days"
                    />
                ) : (
                    chips.length === 0 && <span>—</span>
                )}
            </Box>
        </>
    );
}

/**
 * Stable DataTable cell renderer. Props are passed via column.customerGuaranteeDaysCellProps
 * so react-table does not treat a new component type on every parent render.
 */
function CustomerGuaranteeDaysTableCell({ column, row }) {
    const { onRefresh, setNotification } = column.customerGuaranteeDaysCellProps ?? {};

    return (
        <CustomerGuaranteeDaysCell
            row={row.original}
            onRefresh={onRefresh}
            setNotification={setNotification}
        />
    );
}

export { CustomerGuaranteeDaysCell, CustomerGuaranteeDaysTableCell };
